import { MAGNETWORKS_BEAT_PLANS } from "./magnetworksBeatPlans";
/**
 * WonderLabs R2 — authored-order initial learning-beat coverage for the existing activities plus Magnetworks.
 *
 * Each plan is explicit, not a runtime guess. The four human-reviewed Stage A2
 * examples continue to override these starting maps. Keep one activity as a
 * single beat when it already serves one coherent purpose.
 *
 * IMPORTANT: This is a full-coverage TESTING DRAFT, not an SME-approved
 * pedagogical sequence. Before shipping, review prediction-before-reveal,
 * media, physical safety, optional challenges and evidence gates by mission.
 * A beat is presentation only: all saved keys and original completion rules
 * stay in JourneyPageRenderer.
 */
export const ALL_ACTIVITY_BEAT_PLANS = {
  // Flood Rescue Bridge
  "flood-rescue-prelude:briefing": [
    { id: "focus-1", title: "The Flood Rescue Bridge Mission", kind: "story", blockIds: ["flood-rescue-story-prelude"] },
  ],
  "flood-rescue-prelude:facts": [
    { id: "focus-1", title: "Facts before theories", kind: "predict", blockIds: ["mission-fact-check"] },
  ],
  "rescue-prediction:predict": [
    { id: "focus-1", title: "Predict the capacity", kind: "explain", blockIds: ["wonder-story", "bridge1-capacity-prediction"] },
  ],
  "flat-bridge-test:setup": [
    { id: "focus-1", title: "Set up the same test everyone can compare", kind: "safety", blockIds: ["explore-story", "baseline-checklist"] },
  ],
  "flat-bridge-test:test": [
    { id: "focus-1", title: "Observation, not explanation", kind: "explain", blockIds: ["bridge1-record", "bridge1-observation"] },
  ],
  "hidden-forces:forces": [
    { id: "focus-1", title: "Bring your own evidence into the science", kind: "review", blockIds: ["bridge1-prior-evidence"] },
    { id: "focus-2", title: "The Invisible Forces Inside Your Bridge", kind: "watch", blockIds: ["invisible-forces-video"] },
    { id: "focus-3", title: "Force check", kind: "check", blockIds: ["force-check"] },
  ],
  "hidden-forces:explain": [
    { id: "focus-1", title: "Same Paper, Different Shape", kind: "watch", blockIds: ["same-paper-shape-video"] },
    { id: "focus-2", title: "Explain your Bridge 1 using the science", kind: "explain", blockIds: ["bridge1-science-explanation"] },
  ],
  "engineer-bridge-two:change": [
    { id: "focus-1", title: "Name your one main change", kind: "explain", blockIds: ["apply-story", "design-change"] },
  ],
  "engineer-bridge-two:predict": [
    { id: "focus-1", title: "Predict what the change will do", kind: "explain", blockIds: ["design-prediction"] },
  ],
  "fair-rescue-test:evidence": [
    { id: "focus-1", title: "Bridge 1 is your baseline", kind: "review", blockIds: ["trial1-summary"] },
  ],
  "fair-rescue-test:fair": [
    { id: "focus-1", title: "The Fair-Test Challenge", kind: "watch", blockIds: ["fair-test-video"] },
    { id: "focus-2", title: "Keep the comparison fair", kind: "safety", blockIds: ["fair-test-checklist"] },
  ],
  "fair-rescue-test:test": [
    { id: "focus-1", title: "Record Bridge 2", kind: "record", blockIds: ["bridge2-record"] },
  ],
  "fair-rescue-test:compare": [
    { id: "focus-1", title: "What does the evidence show?", kind: "explain", blockIds: ["comparison-claim"] },
  ],
  "young-engineer-report:review": [
    { id: "focus-1", title: "Failure Leaves Clues", kind: "watch", blockIds: ["failure-clues-video"] },
    { id: "focus-2", title: "Open your engineering evidence", kind: "review", blockIds: ["report-prior-work"] },
  ],
  "young-engineer-report:first": [
    { id: "focus-1", title: "1. What happened to Bridge 1?", kind: "explain", blockIds: ["first-failure-explanation"] },
  ],
  "young-engineer-report:design": [
    { id: "focus-1", title: "2. Why did you make that change?", kind: "explain", blockIds: ["change-explanation"] },
  ],
  "young-engineer-report:defend": [
    { id: "focus-1", title: "3. Defend your conclusion", kind: "explain", blockIds: ["evidence-defense"] },
  ],
  "evidence-reflection:thinking": [
    { id: "focus-1", title: "What changed or strengthened your thinking?", kind: "explain", blockIds: ["reflection-prior-work", "thinking-change"] },
  ],
  "evidence-reflection:failure": [
    { id: "focus-1", title: "What did failure teach you?", kind: "explain", blockIds: ["failure-learning"] },
  ],
  "next-variable-mission:real": [
    { id: "focus-1", title: "From Paper Bridge to Real Bridges", kind: "watch", blockIds: ["real-bridges-video"] },
  ],
  "next-variable-mission:next": [
    { id: "focus-1", title: "Choose one variable for the next test", kind: "predict", blockIds: ["next-variable-choice", "next-test-plan"] },
  ],
  // Mystery Rover
  "wind-car-prelude:green-valley-story-prelude": [
    { id: "focus-1", title: "Enter the Green Valley Emergency", kind: "story", blockIds: ["green-valley-story-prelude"] },
    { id: "focus-2", title: "Medicine must cross the rescue lane", kind: "orient", blockIds: ["rescue-alert"] },
  ],
  "wind-car-prelude:rescue-image": [
    { id: "focus-1", title: "Open the ranger’s emergency message", kind: "observe", blockIds: ["rescue-image"] },
  ],
  "wind-car-wonder:solution-prediction": [
    { id: "focus-1", title: "What might make the rover move?", kind: "predict", blockIds: ["solution-prediction", "initial-theory"] },
  ],
  "wind-car-explore:step2-story": [
    { id: "focus-1", title: "Inspect the visible clues", kind: "observe", blockIds: ["step2-story", "rover-image-gallery"] },
    { id: "focus-2", title: "Explore the 3D Mystery Rover", kind: "inspect", blockIds: ["mainmenu-pwo-block"] },
  ],
  "wind-car-explore:rover-observations": [
    { id: "focus-1", title: "Select the clues you can verify", kind: "check", blockIds: ["rover-observations"] },
  ],
  "wind-car-explore:motion-source": [
    { id: "focus-1", title: "What probably pushes the whole rover?", kind: "predict", blockIds: ["motion-source"] },
    { id: "focus-2", title: "The Science of the Wind Powered Rover", kind: "watch", blockIds: ["science-rover-video-block"] },
    { id: "focus-3", title: "Evidence check", kind: "check", blockIds: ["motor-job-check"] },
  ],
  "wind-car-explore:step2-explanation": [
    { id: "focus-1", title: "Revise the motion theory", kind: "explain", blockIds: ["step2-explanation"] },
  ],
  "wind-car-understand:step3-story": [
    { id: "focus-1", title: "A battery is the beginning, not the whole explanation", kind: "orient", blockIds: ["step3-story"] },
    { id: "focus-2", title: "Open the Virtual Parts Tray", kind: "inspect", blockIds: ["accessories-pwo-block"] },
    { id: "focus-3", title: "Compare the virtual and real kit", kind: "observe", blockIds: ["parts-gallery"] },
    { id: "focus-4", title: "Inventory the real kit", kind: "check", blockIds: ["parts-found"] },
  ],
  "wind-car-understand:component-jobs": [
    { id: "focus-1", title: "Match each part to its main job", kind: "match", blockIds: ["component-jobs"] },
  ],
  "wind-car-understand:system-video-block": [
    { id: "focus-1", title: "The Wind Powered Rover", kind: "watch", blockIds: ["system-video-block"] },
  ],
  "wind-car-understand:power-path": [
    { id: "focus-1", title: "Arrange the rover’s energy-and-motion path", kind: "model", blockIds: ["power-path"] },
    { id: "focus-2", title: "Circuit check", kind: "check", blockIds: ["closed-circuit-check"] },
    { id: "focus-3", title: "Explain where motion begins", kind: "explain", blockIds: ["power-explanation"] },
  ],
  "wind-car-understand:step4-story": [
    { id: "focus-1", title: "Read the direction arrows", kind: "observe", blockIds: ["step4-story", "air-diagram"] },
    { id: "focus-2", title: "Which way should the propeller send the air?", kind: "predict", blockIds: ["air-prediction"] },
  ],
  "wind-car-understand:backward-push-video-block": [
    { id: "focus-1", title: "The Backward Push Paradox", kind: "watch", blockIds: ["backward-push-video-block"] },
    { id: "focus-2", title: "Optional Action–Reaction Exploration", kind: "inspect", blockIds: ["balloon-pwo-block"] },
    { id: "focus-3", title: "Record what the moving air did", kind: "explain", blockIds: ["demo-observation"] },
  ],
  "wind-car-understand:thrust-check": [
    { id: "focus-1", title: "Direction check", kind: "check", blockIds: ["thrust-check"] },
    { id: "focus-2", title: "Prediction: reverse the motor direction", kind: "check", blockIds: ["reverse-wire-check"] },
    { id: "focus-3", title: "Resolve the paradox", kind: "explain", blockIds: ["backward-explanation"] },
  ],
  "wind-car-apply:step5-story": [
    { id: "focus-1", title: "No batteries until the mechanical checks are complete", kind: "orient", blockIds: ["step5-story"] },
    { id: "focus-2", title: "Inspect the actual kit and guide", kind: "observe", blockIds: ["readiness-gallery"] },
    { id: "focus-3", title: "Confirm the mission-critical parts", kind: "check", blockIds: ["parts-readiness"] },
    { id: "focus-4", title: "Team safety authorization", kind: "safety", blockIds: ["safety-checks"] },
    { id: "focus-5", title: "Which problem is most likely to stop your first run?", kind: "predict", blockIds: ["failure-prediction"] },
  ],
  "wind-car-apply:build-plan-note": [
    { id: "focus-1", title: "Write the team’s prevention plan", kind: "explain", blockIds: ["build-plan-note"] },
  ],
  "wind-car-apply:step6-story": [
    { id: "focus-1", title: "Build with power disconnected", kind: "orient", blockIds: ["step6-story"] },
    { id: "focus-2", title: "Open the 3D Installation Guide", kind: "inspect", blockIds: ["installation-pwo-block"] },
    { id: "focus-3", title: "Compare the installation references", kind: "observe", blockIds: ["installation-gallery"] },
  ],
  "wind-car-apply:assembly-checklist": [
    { id: "focus-1", title: "Guided assembly record", kind: "safety", blockIds: ["assembly-checklist"] },
    { id: "focus-2", title: "Mechanical freedom check", kind: "safety", blockIds: ["wheel-check"] },
    { id: "focus-3", title: "Safety confirmation", kind: "check", blockIds: ["power-off-confirm"] },
  ],
  "wind-car-apply:assembly-note": [
    { id: "focus-1", title: "Record one build challenge or successful check", kind: "explain", blockIds: ["assembly-note"] },
  ],
  "wind-car-test-improve:step7-story": [
    { id: "focus-1", title: "Observe first; adjust only after power is OFF", kind: "orient", blockIds: ["step7-story"] },
    { id: "focus-2", title: "Use the safe diagnostic sequence", kind: "observe", blockIds: ["diagnostic-image"] },
    { id: "focus-3", title: "Review the assembly evidence", kind: "review", blockIds: ["build-summary"] },
    { id: "focus-4", title: "Record the first powered test", kind: "record", blockIds: ["first-spin-record"] },
  ],
  "wind-car-test-improve:open-circuit-diagnostic": [
    { id: "focus-1", title: "Circuit diagnostic", kind: "check", blockIds: ["open-circuit-diagnostic"] },
    { id: "focus-2", title: "Choose the next diagnostic action", kind: "explain", blockIds: ["rover-master-teacher-diagnostic", "diagnostic-note"] },
  ],
  "wind-car-test-improve:step8-story": [
    { id: "focus-1", title: "A result is useful only when the test conditions are recorded", kind: "orient", blockIds: ["step8-story"] },
    { id: "focus-2", title: "Prepare the rescue lane", kind: "observe", blockIds: ["lane-image"] },
    { id: "focus-3", title: "Bring forward your predictions and diagnostic", kind: "review", blockIds: ["trial-prior-work"] },
    { id: "focus-4", title: "Predict the first run", kind: "predict", blockIds: ["trial1-prediction"] },
  ],
  "wind-car-test-improve:trial1-record": [
    { id: "focus-1", title: "Trial 1 evidence record", kind: "record", blockIds: ["trial1-record"] },
    { id: "focus-2", title: "Optional: The Friction Trap", kind: "watch", blockIds: ["friction-video-block"] },
    { id: "focus-3", title: "Evidence check: surface and rubbing", kind: "check", blockIds: ["friction-check"] },
    { id: "focus-4", title: "Describe what the rover actually did", kind: "explain", blockIds: ["trial1-observation"] },
  ],
  "wind-car-test-improve:first-run-problem": [
    { id: "focus-1", title: "Identify the strongest improvement opportunity", kind: "explain", blockIds: ["first-run-problem"] },
  ],
  "wind-car-test-improve:step9-story": [
    { id: "focus-1", title: "One change. Same test. New evidence.", kind: "orient", blockIds: ["step9-story"] },
    { id: "focus-2", title: "Plan the fair comparison", kind: "observe", blockIds: ["fair-test-image"] },
    { id: "focus-3", title: "Review the baseline", kind: "review", blockIds: ["trial1-summary"] },
    { id: "focus-4", title: "Choose ONE redesign variable", kind: "predict", blockIds: ["redesign-variable", "redesign-prediction"] },
    { id: "focus-5", title: "Optional: Taming the Thrust", kind: "watch", blockIds: ["taming-thrust-video-block"] },
  ],
  "wind-car-test-improve:trial2-record": [
    { id: "focus-1", title: "Trial 2 evidence record", kind: "record", blockIds: ["trial2-record"] },
  ],
  "wind-car-test-improve:comparison-claim": [
    { id: "focus-1", title: "Compare Trial 1 and Trial 2", kind: "explain", blockIds: ["comparison-claim"] },
    { id: "focus-2", title: "What is your engineering conclusion?", kind: "predict", blockIds: ["redesign-conclusion"] },
  ],
  "wind-car-explain:step10-story": [
    { id: "focus-1", title: "Use the final system model", kind: "observe", blockIds: ["step10-story", "final-chain-image"] },
    { id: "focus-2", title: "Bring the evidence into the report", kind: "review", blockIds: ["final-prior-work"] },
  ],
  "wind-car-explain:final-energy-chain": [
    { id: "focus-1", title: "Rebuild the complete cause-and-effect chain", kind: "model", blockIds: ["final-energy-chain"] },
  ],
  "wind-car-explain:engineer-report": [
    { id: "focus-1", title: "Prepare the Engineer’s Rescue Report", kind: "record", blockIds: ["engineer-report"] },
  ],
  "wind-car-reflect:reflect-story": [
    { id: "focus-1", title: "Review your evidence trail", kind: "review", blockIds: ["reflect-story", "reflect-prior-work"] },
  ],
  "wind-car-reflect:final-reflection": [
    { id: "focus-1", title: "Reflect on your thinking", kind: "explain", blockIds: ["final-reflection"] },
  ],
  "wind-car-grow:grow-story": [
    { id: "focus-1", title: "Connect the rescue rover to bigger engineering ideas", kind: "orient", blockIds: ["grow-story"] },
    { id: "focus-2", title: "Optional Contrast: How a Real Wind Turbine Works", kind: "watch", blockIds: ["real-wind-video-block"] },
    { id: "focus-3", title: "Scientific honesty check", kind: "check", blockIds: ["honesty-check"] },
  ],
  // Glow City
  "conductive-dough-prelude:glow-city-story-prelude": [
    { id: "focus-1", title: "Enter the Great Glow City Blackout", kind: "story", blockIds: ["glow-city-story-prelude"] },
    { id: "focus-2", title: "The Festival of Light cannot open", kind: "orient", blockIds: ["blackout-alert"] },
  ],
  "conductive-dough-prelude:blackout-reveal": [
    { id: "focus-1", title: "Inspect the last working landmark", kind: "observe", blockIds: ["blackout-reveal", "evidence-gallery"] },
  ],
  "conductive-dough-wonder:opening-cause": [
    { id: "focus-1", title: "What might make every light go dark?", kind: "predict", blockIds: ["opening-cause"] },
    { id: "focus-2", title: "Start the Glow City case file", kind: "record", blockIds: ["opening-observations"] },
  ],
  "conductive-dough-explore:step2-story": [
    { id: "focus-1", title: "Can a soft material behave like a wire?", kind: "orient", blockIds: ["step2-story"] },
    { id: "focus-2", title: "Predict conductors and insulators", kind: "reason", blockIds: ["material-prediction"] },
    { id: "focus-3", title: "Inspect the virtual materials tray", kind: "inspect", blockIds: ["mainmenu-pwo", "materials-pwo"] },
    { id: "focus-4", title: "Read the material evidence", kind: "observe", blockIds: ["material-gallery"] },
  ],
  "conductive-dough-explore:material-jobs": [
    { id: "focus-1", title: "Match each material to its main job", kind: "match", blockIds: ["material-jobs"] },
  ],
  "conductive-dough-explore:road-explanation": [
    { id: "focus-1", title: "Explain the first clue", kind: "explain", blockIds: ["road-explanation"] },
  ],
  "conductive-dough-explore:step3-story": [
    { id: "focus-1", title: "Why are there two separate dough pieces?", kind: "predict", blockIds: ["step3-story", "two-road-prediction"] },
  ],
  "conductive-dough-explore:step1-pwo": [
    { id: "focus-1", title: "Connect the 4-AA battery leads", kind: "inspect", blockIds: ["step1-pwo", "step2-pwo"] },
    { id: "focus-2", title: "Inspect the circuit map", kind: "observe", blockIds: ["two-road-gallery"] },
    { id: "focus-3", title: "Trace the complete loop", kind: "model", blockIds: ["loop-sequence"] },
  ],
  "conductive-dough-explore:gap-reason": [
    { id: "focus-1", title: "Defend the separation", kind: "explain", blockIds: ["gap-reason"] },
  ],
  "conductive-dough-understand:step4-story": [
    { id: "focus-1", title: "The path looks complete, but the LED stays dark", kind: "orient", blockIds: ["step4-story"] },
    { id: "focus-2", title: "What will happen if the LED legs are swapped?", kind: "predict", blockIds: ["polarity-prediction"] },
    { id: "focus-3", title: "Inspect the one-way gate", kind: "observe", blockIds: ["polarity-gallery"] },
  ],
  "conductive-dough-understand:step3-pwo": [
    { id: "focus-1", title: "Swap the LED terminals", kind: "inspect", blockIds: ["step3-pwo", "step4-pwo"] },
    { id: "focus-2", title: "Video evidence: The Mystery of the Dark LED", kind: "watch", blockIds: ["dark-led-video"] },
  ],
  "conductive-dough-understand:polarity-record": [
    { id: "focus-1", title: "Record the virtual polarity test", kind: "record", blockIds: ["polarity-record"] },
    { id: "focus-2", title: "Glow City diagnostic check", kind: "check", blockIds: ["polarity-check"] },
  ],
  "conductive-dough-apply:step5-story": [
    { id: "focus-1", title: "Simple and reliable comes before spectacular", kind: "orient", blockIds: ["step5-story"] },
    { id: "focus-2", title: "Match the real kit to the virtual evidence", kind: "observe", blockIds: ["kit-gallery"] },
    { id: "focus-3", title: "Open the virtual assembly menu", kind: "inspect", blockIds: ["assembly-pwo"] },
    { id: "focus-4", title: "Inventory the physical kit", kind: "check", blockIds: ["kit-inventory"] },
    { id: "focus-5", title: "Power-off build authorization", kind: "safety", blockIds: ["physical-safety"] },
  ],
  "conductive-dough-apply:first-light-build": [
    { id: "focus-1", title: "Build the first Glow City bridge", kind: "safety", blockIds: ["first-light-build"] },
  ],
  "conductive-dough-apply:first-light-record": [
    { id: "focus-1", title: "Record the first physical test", kind: "record", blockIds: ["first-light-record"] },
  ],
  "conductive-dough-test-improve:step6-story": [
    { id: "focus-1", title: "The current found a bypass", kind: "orient", blockIds: ["step6-story"] },
    { id: "focus-2", title: "Predict the touching-path result", kind: "predict", blockIds: ["shortcut-prediction"] },
    { id: "focus-3", title: "Trace the competing routes", kind: "observe", blockIds: ["shortcut-gallery"] },
    { id: "focus-4", title: "Optional evidence video: Diagnosing Short Circuits", kind: "watch", blockIds: ["short-video"] },
  ],
  "conductive-dough-test-improve:fault-classification": [
    { id: "focus-1", title: "Classify the Glow City faults", kind: "reason", blockIds: ["fault-classification", "shortcut-sequence"] },
    { id: "focus-2", title: "Write the safe repair plan", kind: "explain", blockIds: ["safe-short-fix"] },
  ],
  "conductive-dough-test-improve:step7-story": [
    { id: "focus-1", title: "A wall controls where current must not go", kind: "orient", blockIds: ["step7-story"] },
    { id: "focus-2", title: "Where should the insulating dough go?", kind: "predict", blockIds: ["wall-placement"] },
    { id: "focus-3", title: "Inspect the structural clue", kind: "observe", blockIds: ["wall-gallery"] },
    { id: "focus-4", title: "Explore alternate dough shapes", kind: "inspect", blockIds: ["exploremore-pwo"] },
  ],
  "conductive-dough-test-improve:wall-jobs": [
    { id: "focus-1", title: "Assign the insulating wall its jobs", kind: "match", blockIds: ["wall-jobs"] },
    { id: "focus-2", title: "Add a protective insulating wall", kind: "safety", blockIds: ["wall-build-check"] },
    { id: "focus-3", title: "Compare before and after the wall", kind: "record", blockIds: ["wall-comparison"] },
  ],
  "conductive-dough-test-improve:step8-story": [
    { id: "focus-1", title: "Make the sculpture beautiful because the circuit is reliable", kind: "orient", blockIds: ["step8-story"] },
    { id: "focus-2", title: "Gather design inspiration", kind: "observe", blockIds: ["landmark-gallery"] },
    { id: "focus-3", title: "Choose your Glow City landmark", kind: "predict", blockIds: ["landmark-type"] },
    { id: "focus-4", title: "Engineer the design brief", kind: "reason", blockIds: ["criteria-constraints"] },
  ],
  "conductive-dough-test-improve:design-plan": [
    { id: "focus-1", title: "Write the landmark blueprint", kind: "record", blockIds: ["design-plan"] },
    { id: "focus-2", title: "Build the Glow City landmark", kind: "safety", blockIds: ["landmark-build"] },
    { id: "focus-3", title: "Record Landmark Trial 1", kind: "record", blockIds: ["landmark-first-trial"] },
  ],
  "conductive-dough-test-improve:step9-story": [
    { id: "focus-1", title: "A failure is evidence, not defeat", kind: "orient", blockIds: ["step9-story"] },
    { id: "focus-2", title: "Use the Glow City diagnostic order", kind: "observe", blockIds: ["troubleshooting-image"] },
    { id: "focus-3", title: "Review Trial 1 evidence", kind: "review", blockIds: ["trial1-summary"] },
    { id: "focus-4", title: "Diagnose the evidence", kind: "reason", blockIds: ["fault-hypothesis"] },
  ],
  "conductive-dough-test-improve:redesign-variable": [
    { id: "focus-1", title: "Choose one variable to change", kind: "predict", blockIds: ["redesign-variable", "redesign-prediction"] },
  ],
  "conductive-dough-test-improve:trial2-record": [
    { id: "focus-1", title: "Judge the redesign using evidence", kind: "explain", blockIds: ["trial2-record", "redesign-conclusion"] },
  ],
  "conductive-dough-explain:step10-story": [
    { id: "focus-1", title: "Prepare the final Glow City report", kind: "observe", blockIds: ["step10-story", "final-gallery"] },
    { id: "focus-2", title: "Bring forward the rescue evidence", kind: "review", blockIds: ["prior-evidence"] },
  ],
  "conductive-dough-explain:final-chain": [
    { id: "focus-1", title: "Build the final causal chain", kind: "model", blockIds: ["final-chain"] },
    { id: "focus-2", title: "Scientific honesty check", kind: "check", blockIds: ["honesty-check"] },
    { id: "focus-3", title: "Write the Circuit Sculptor Rescue Report", kind: "record", blockIds: ["rescue-report"] },
  ],
  "conductive-dough-reflect:conductive-reflect-story": [
    { id: "focus-1", title: "Reflect on your Circuit Sculptor journey", kind: "explain", blockIds: ["conductive-reflect-story", "final-reflection"] },
  ],
  "conductive-dough-grow:conductive-grow-story": [
    { id: "focus-1", title: "Design the next Glow City idea", kind: "explain", blockIds: ["conductive-grow-story", "conductive-grow-transfer"] },
  ],
  // Breeze Station
  "electric-spiral-prelude:breeze-station-story-prelude": [
    { id: "focus-1", title: "The Day Breeze Station Fell Silent", kind: "story", blockIds: ["breeze-station-story-prelude"] },
    { id: "focus-2", title: "Two machines failed before camp began", kind: "orient", blockIds: ["station-alert"] },
  ],
  "electric-spiral-prelude:station-reveal": [
    { id: "focus-1", title: "Inspect the two unopened engineering kits", kind: "observe", blockIds: ["station-reveal", "two-kits-gallery"] },
  ],
  "electric-spiral-wonder:opening-cause": [
    { id: "focus-1", title: "What could explain both failures?", kind: "predict", blockIds: ["opening-cause"] },
    { id: "focus-2", title: "Start the Breeze Station case file", kind: "record", blockIds: ["opening-record"] },
  ],
  "electric-spiral-explore:path-story": [
    { id: "focus-1", title: "Leave and return", kind: "orient", blockIds: ["path-story"] },
    { id: "focus-2", title: "What will a small gap do in this low-voltage kit?", kind: "predict", blockIds: ["gap-prediction"] },
    { id: "focus-3", title: "Compare open and closed paths", kind: "observe", blockIds: ["path-gallery"] },
  ],
  "electric-spiral-explore:loop-sequence": [
    { id: "focus-1", title: "Build the complete path in order", kind: "model", blockIds: ["loop-sequence"] },
    { id: "focus-2", title: "Apprentice circuit check", kind: "check", blockIds: ["switch-check"] },
    { id: "focus-3", title: "Write the hidden rule in your own words", kind: "explain", blockIds: ["loop-explanation"] },
  ],
  "electric-spiral-explore:parta-story": [
    { id: "focus-1", title: "Study the exact Part A kit and guide", kind: "observe", blockIds: ["parta-story", "parta-gallery"] },
    { id: "focus-2", title: "Part A power-off readiness check", kind: "safety", blockIds: ["parta-readiness"] },
  ],
  "electric-spiral-explore:parta-build": [
    { id: "focus-1", title: "Build the signal beacon", kind: "safety", blockIds: ["parta-build"] },
    { id: "focus-2", title: "Record the signal-beacon test", kind: "record", blockIds: ["parta-trial"] },
    { id: "focus-3", title: "Signal-beacon diagnostic", kind: "check", blockIds: ["parta-check"] },
  ],
  "electric-spiral-understand:bridge-story": [
    { id: "focus-1", title: "Discover how light and motion differ", kind: "orient", blockIds: ["bridge-story"] },
    { id: "focus-2", title: "Classify the two systems", kind: "reason", blockIds: ["same-different"] },
    { id: "focus-3", title: "Study the two-machine comparison", kind: "observe", blockIds: ["comparison-gallery"] },
  ],
  "electric-spiral-understand:parta-chain": [
    { id: "focus-1", title: "Trace the light circuit energy chain", kind: "model", blockIds: ["parta-chain"] },
    { id: "focus-2", title: "Predict the motor and fan energy chain", kind: "model", blockIds: ["partb-predicted-chain"] },
    { id: "focus-3", title: "Watch the complete power loop", kind: "watch", blockIds: ["power-loop-video"] },
  ],
  "electric-spiral-understand:bridge-explanation": [
    { id: "focus-1", title: "State the bridge rule", kind: "explain", blockIds: ["bridge-explanation"] },
  ],
  "electric-spiral-understand:partb-reveal-story": [
    { id: "focus-1", title: "Name the jobs before touching the parts", kind: "orient", blockIds: ["partb-reveal-story"] },
    { id: "focus-2", title: "Inspect the virtual material tray", kind: "inspect", blockIds: ["partb-mainmenu", "partb-materials"] },
    { id: "focus-3", title: "Compare virtual and physical evidence", kind: "observe", blockIds: ["partb-gallery"] },
    { id: "focus-4", title: "Match each component to its main job", kind: "match", blockIds: ["component-jobs"] },
  ],
  "electric-spiral-understand:component-explanation": [
    { id: "focus-1", title: "Predict the machine's weakest point", kind: "explain", blockIds: ["component-explanation"] },
  ],
  "electric-spiral-apply:tower-story": [
    { id: "focus-1", title: "Use both physical and virtual assembly evidence", kind: "observe", blockIds: ["tower-story", "assembly-gallery"] },
    { id: "focus-2", title: "Open the six-step virtual assembly guide", kind: "inspect", blockIds: ["assembly-pwo"] },
  ],
  "electric-spiral-apply:partb-safety": [
    { id: "focus-1", title: "Part B power-off safety gate", kind: "safety", blockIds: ["partb-safety", "partb-build"] },
  ],
  "electric-spiral-apply:assembly-risk": [
    { id: "focus-1", title: "Which issue is mainly mechanical?", kind: "predict", blockIds: ["assembly-risk", "assembly-note"] },
  ],
  "electric-spiral-test-improve:wake-story": [
    { id: "focus-1", title: "A spinning blade is only one piece of evidence", kind: "orient", blockIds: ["wake-story"] },
    { id: "focus-2", title: "Plan the first wind test", kind: "observe", blockIds: ["test-gallery"] },
    { id: "focus-3", title: "What should happen when the circuit closes?", kind: "predict", blockIds: ["first-run-prediction"] },
    { id: "focus-4", title: "Teacher-approved first power sequence", kind: "safety", blockIds: ["first-run-checklist"] },
  ],
  "electric-spiral-test-improve:first-run-record": [
    { id: "focus-1", title: "Record three kinds of evidence", kind: "record", blockIds: ["first-run-record"] },
    { id: "focus-2", title: "Build the observed Wind Engine chain", kind: "model", blockIds: ["wind-chain"] },
    { id: "focus-3", title: "Open the PWO science summary", kind: "inspect", blockIds: ["learned-pwo"] },
  ],
  "electric-spiral-test-improve:reverse-story": [
    { id: "focus-1", title: "The machine is awake—but is the breeze going where you expected?", kind: "orient", blockIds: ["reverse-story"] },
    { id: "focus-2", title: "Study the controlled comparison", kind: "observe", blockIds: ["reverse-gallery"] },
    { id: "focus-3", title: "What should change after swapping motor leads?", kind: "predict", blockIds: ["reverse-prediction"] },
    { id: "focus-4", title: "Keep the test fair", kind: "reason", blockIds: ["fair-test-plan"] },
  ],
  "electric-spiral-test-improve:direction-safety": [
    { id: "focus-1", title: "Power-off direction-test gate", kind: "safety", blockIds: ["direction-safety"] },
    { id: "focus-2", title: "Compare Test 1 and Test 2", kind: "record", blockIds: ["direction-record"] },
  ],
  "electric-spiral-test-improve:reverse-check": [
    { id: "focus-1", title: "Direction mystery check", kind: "check", blockIds: ["reverse-check"] },
    { id: "focus-2", title: "Optional enrichment: The Power vs Direction Paradox", kind: "watch", blockIds: ["direction-video"] },
  ],
  "electric-spiral-test-improve:rescue-story": [
    { id: "focus-1", title: "Silent and shaky are different clues", kind: "orient", blockIds: ["rescue-story"] },
    { id: "focus-2", title: "Use the five-check rescue flow", kind: "observe", blockIds: ["troubleshoot-gallery"] },
    { id: "focus-3", title: "Electrical or mechanical clue?", kind: "reason", blockIds: ["fault-classification"] },
    { id: "focus-4", title: "Order the safe diagnostic checks", kind: "model", blockIds: ["diagnostic-sequence"] },
    { id: "focus-5", title: "Review your first-run evidence", kind: "review", blockIds: ["trial1-summary"] },
  ],
  "electric-spiral-test-improve:fault-hypothesis": [
    { id: "focus-1", title: "Choose the strongest current hypothesis", kind: "predict", blockIds: ["fault-hypothesis"] },
  ],
  "electric-spiral-test-improve:redesign-record": [
    { id: "focus-1", title: "Record one change and two trials", kind: "record", blockIds: ["redesign-record"] },
    { id: "focus-2", title: "Optional expert evidence: Circuit Diagnostics", kind: "watch", blockIds: ["diagnostics-video"] },
    { id: "focus-3", title: "Rescue safety check", kind: "check", blockIds: ["safety-quiz"] },
  ],
  "electric-spiral-explain:final-story": [
    { id: "focus-1", title: "Review the restored station and two-machine chain", kind: "observe", blockIds: ["final-story", "final-gallery"] },
    { id: "focus-2", title: "Bring forward your strongest evidence", kind: "review", blockIds: ["prior-evidence"] },
  ],
  "electric-spiral-explain:final-chain": [
    { id: "focus-1", title: "Build the final Wind Engine causal chain", kind: "model", blockIds: ["final-chain"] },
    { id: "focus-2", title: "Final science honesty check", kind: "check", blockIds: ["final-honesty"] },
    { id: "focus-3", title: "Deliver the Breeze Engineer Report", kind: "record", blockIds: ["engineer-report"] },
  ],
  "electric-spiral-reflect:electric-reflect-story": [
    { id: "focus-1", title: "Reflect on Day 1", kind: "explain", blockIds: ["electric-reflect-story", "final-reflection"] },
  ],
  "electric-spiral-grow:electric-grow-story": [
    { id: "focus-1", title: "Choose a new machine", kind: "explain", blockIds: ["electric-grow-story", "electric-grow-transfer"] },
  ],
  // Robot Artist
  "robot-artist-prelude:robot-artist-story-prelude": [
    { id: "focus-1", title: "Enter the Mystery of the Robot Artist", kind: "story", blockIds: ["robot-artist-story-prelude"] },
    { id: "focus-2", title: "Investigator rule check", kind: "predict", blockIds: ["robot-prelude-rule-check"] },
  ],
  "robot-artist-wonder:case-update": [
    { id: "focus-1", title: "Reveal the mystery artist", kind: "observe", blockIds: ["case-update", "robot-reveal"] },
  ],
  "robot-artist-wonder:theory-comparison": [
    { id: "focus-1", title: "How did the new evidence compare with your theory?", kind: "predict", blockIds: ["theory-comparison", "clue-now-makes-sense"] },
  ],
  "robot-artist-wonder:robot-abilities": [
    { id: "focus-1", title: "What must a robot artist be able to do?", kind: "check", blockIds: ["robot-abilities"] },
    { id: "focus-2", title: "Engineer the mission", kind: "reason", blockIds: ["criteria-constraints"] },
    { id: "focus-3", title: "Choose your first engineering goal", kind: "predict", blockIds: ["mission-goal"] },
  ],
  "robot-artist-wonder:engineering-video": [
    { id: "focus-1", title: "How can a machine move without wheels?", kind: "watch", blockIds: ["engineering-video"] },
    { id: "focus-2", title: "Meet the virtual Drawing Robot", kind: "inspect", blockIds: ["meet-robot-pwo"] },
  ],
  "robot-artist-explore:step3-story": [
    { id: "focus-1", title: "A machine is more than a pile of parts", kind: "orient", blockIds: ["step3-story"] },
    { id: "focus-2", title: "Explore the virtual evidence tray", kind: "inspect", blockIds: ["accessories-pwo"] },
    { id: "focus-3", title: "Compare three views of the evidence", kind: "observe", blockIds: ["kit-evidence-gallery"] },
    { id: "focus-4", title: "Inventory your real kit", kind: "check", blockIds: ["real-kit-check"] },
  ],
  "robot-artist-explore:part-systems": [
    { id: "focus-1", title: "Sort the parts into robot systems", kind: "reason", blockIds: ["part-systems", "part-jobs"] },
  ],
  "robot-artist-explore:mystery-weight-hypothesis": [
    { id: "focus-1", title: "The mystery part", kind: "predict", blockIds: ["mystery-weight-hypothesis", "mystery-weight-reason"] },
  ],
  "robot-artist-explore:student-kit-question": [
    { id: "focus-1", title: "Write your own investigation question", kind: "explain", blockIds: ["student-kit-question"] },
  ],
  "robot-artist-understand:step4-story": [
    { id: "focus-1", title: "The robot is ready—but all its energy is still hidden", kind: "orient", blockIds: ["step4-story"] },
    { id: "focus-2", title: "Where does the movement story begin?", kind: "predict", blockIds: ["energy-start-prediction"] },
    { id: "focus-3", title: "Read the engineering evidence", kind: "observe", blockIds: ["energy-evidence-gallery"] },
    { id: "focus-4", title: "What is energy?", kind: "watch", blockIds: ["energy-intro-video"] },
    { id: "focus-5", title: "Look inside the robot's power source", kind: "inspect", blockIds: ["energy-forms-pwo", "battery-pwo"] },
  ],
  "robot-artist-understand:energy-pathway": [
    { id: "focus-1", title: "Build the robot's energy pathway", kind: "model", blockIds: ["energy-pathway", "system-energy-jobs"] },
    { id: "focus-2", title: "Science check", kind: "check", blockIds: ["motor-energy-check"] },
  ],
  "robot-artist-understand:motion-plan": [
    { id: "focus-1", title: "Predict the robot's first motion", kind: "predict", blockIds: ["motion-plan", "art-pattern-plan"] },
    { id: "focus-2", title: "Explain where movement begins", kind: "explain", blockIds: ["power-plan-evidence"] },
  ],
  "robot-artist-understand:step5-story": [
    { id: "focus-1", title: "Inspect the circuit evidence", kind: "observe", blockIds: ["step5-story", "circuit-images"] },
    { id: "focus-2", title: "Where would you investigate first?", kind: "predict", blockIds: ["circuit-first-prediction"] },
  ],
  "robot-artist-understand:circuit-video": [
    { id: "focus-1", title: "Electricity Traffic Needs a Complete Road", kind: "watch", blockIds: ["circuit-video"] },
    { id: "focus-2", title: "Open and Close the Electrical Road", kind: "inspect", blockIds: ["closed-switch-pwo"] },
    { id: "focus-3", title: "Trace one complete loop", kind: "model", blockIds: ["circuit-path-sequence", "circuit-jobs"] },
    { id: "focus-4", title: "Circuit detective check", kind: "check", blockIds: ["open-wire-check"] },
  ],
  "robot-artist-understand:physical-circuit-checklist": [
    { id: "focus-1", title: "Build and test the real circuit", kind: "safety", blockIds: ["physical-circuit-checklist"] },
    { id: "focus-2", title: "Write the diagnosis", kind: "explain", blockIds: ["circuit-evidence"] },
  ],
  "robot-artist-understand:step6-story": [
    { id: "focus-1", title: "Compare centered and off-center motion", kind: "observe", blockIds: ["step6-story", "wobble-images"] },
    { id: "focus-2", title: "Which design should create stronger vibration?", kind: "predict", blockIds: ["balanced-offcenter-prediction"] },
  ],
  "robot-artist-understand:dancing-robot-video": [
    { id: "focus-1", title: "The Physics of the Dancing Robot", kind: "watch", blockIds: ["dancing-robot-video"] },
    { id: "focus-2", title: "Investigate Inertia", kind: "inspect", blockIds: ["motor-animation-pwo", "inertia-pwo"] },
  ],
  "robot-artist-understand:wobble-sequence": [
    { id: "focus-1", title: "Build the wobble mechanism", kind: "model", blockIds: ["wobble-sequence", "motion-causes"] },
    { id: "focus-2", title: "Wobble check", kind: "check", blockIds: ["center-weight-check"] },
    { id: "focus-3", title: "Explain the dance", kind: "explain", blockIds: ["wobble-evidence"] },
  ],
  "robot-artist-apply:step7-story": [
    { id: "focus-1", title: "Build carefully. Predict before switching on.", kind: "orient", blockIds: ["step7-story"] },
    { id: "focus-2", title: "Study the assembly evidence", kind: "observe", blockIds: ["assembly-gallery"] },
    { id: "focus-3", title: "Engineering the Doodle Robot", kind: "watch", blockIds: ["assembly-video"] },
    { id: "focus-4", title: "Open the Interactive Assembly Guide", kind: "inspect", blockIds: ["installation-pwo", "doodle-robot-threejs-pilot"] },
  ],
  "robot-artist-apply:assembly-phase-checklist": [
    { id: "focus-1", title: "Prototype assembly tracker", kind: "safety", blockIds: ["assembly-phase-checklist"] },
    { id: "focus-2", title: "Predict the maiden voyage", kind: "predict", blockIds: ["launch-prediction"] },
  ],
  "robot-artist-apply:first-test-record": [
    { id: "focus-1", title: "Record Test 1", kind: "record", blockIds: ["first-test-record"] },
    { id: "focus-2", title: "Read the artwork as data", kind: "explain", blockIds: ["drawing-as-data"] },
    { id: "focus-3", title: "Mapping the Wobble", kind: "watch", blockIds: ["mapping-wobble-video"] },
  ],
  "robot-artist-test-improve:step8-story": [
    { id: "focus-1", title: "Reopen the Test 1 case file", kind: "review", blockIds: ["step8-story", "test1-case-file"] },
    { id: "focus-2", title: "Study the redesign clues", kind: "observe", blockIds: ["redesign-gallery"] },
    { id: "focus-3", title: "Choose exactly one variable", kind: "predict", blockIds: ["redesign-variable", "redesign-prediction"] },
  ],
  "robot-artist-test-improve:redesign-video": [
    { id: "focus-1", title: "Optional: Master Your Wobble Power", kind: "watch", blockIds: ["redesign-video", "wobble-power-video"] },
    { id: "focus-2", title: "Optional Friction Science Station", kind: "inspect", blockIds: ["friction-station"] },
  ],
  "robot-artist-test-improve:test2-record": [
    { id: "focus-1", title: "Record Test 2", kind: "record", blockIds: ["test2-record"] },
    { id: "focus-2", title: "Judge the redesign against your goal", kind: "predict", blockIds: ["improvement-conclusion", "test-comparison"] },
  ],
  "robot-artist-explain:step9-story": [
    { id: "focus-1", title: "Evidence from your two tests", kind: "review", blockIds: ["step9-story", "tests-summary"] },
    { id: "focus-2", title: "See the robot as one connected system", kind: "observe", blockIds: ["system-gallery"] },
  ],
  "robot-artist-explain:energy-motion-sequence": [
    { id: "focus-1", title: "Build the complete cause-and-effect chain", kind: "model", blockIds: ["energy-motion-sequence", "system-jobs"] },
    { id: "focus-2", title: "Optional Recap: Electricity Needs a Complete Road", kind: "watch", blockIds: ["imbalance-recap-video", "circuit-recap-video"] },
  ],
  "robot-artist-explain:circuit-defense": [
    { id: "focus-1", title: "Defense check: electrical pathway", kind: "check", blockIds: ["circuit-defense"] },
    { id: "focus-2", title: "Defense check: vibration pathway", kind: "check", blockIds: ["motion-defense"] },
  ],
  "robot-artist-explain:final-science-explanation": [
    { id: "focus-1", title: "Write the complete system explanation", kind: "explain", blockIds: ["final-science-explanation"] },
    { id: "focus-2", title: "Support the explanation with test evidence", kind: "explain", blockIds: ["evidence-for-explanation"] },
  ],
  "robot-artist-reflect:robot-reflect-story": [
    { id: "focus-1", title: "Final reflection", kind: "explain", blockIds: ["robot-reflect-story", "final-case-summary", "final-reflection"] },
  ],
  "robot-artist-grow:step10-story": [
    { id: "focus-1", title: "You are the engineer and the artist", kind: "orient", blockIds: ["step10-story"] },
    { id: "focus-2", title: "Prepare the final reveal", kind: "observe", blockIds: ["gallery-images"] },
    { id: "focus-3", title: "Name your robot artist", kind: "explain", blockIds: ["robot-name"] },
    { id: "focus-4", title: "Title the gallery artwork", kind: "explain", blockIds: ["artwork-title"] },
  ],
  "robot-artist-grow:gallery-piece": [
    { id: "focus-1", title: "Select the gallery evidence", kind: "predict", blockIds: ["gallery-piece"] },
  ],
  "robot-artist-grow:gallery-presentation": [
    { id: "focus-1", title: "Build the gallery presentation", kind: "record", blockIds: ["gallery-presentation"] },
    { id: "focus-2", title: "Engineer's defense challenge", kind: "check", blockIds: ["engineers-defense"] },
    { id: "focus-3", title: "Complete the Virtual Drawing Robot Journey", kind: "inspect", blockIds: ["completion-pwo"] },
  ],
  // Magnetworks: explicitly reviewed focused beats (see separate source module).
  ...MAGNETWORKS_BEAT_PLANS,
};
